'use client';
import { Modal } from '@benzinga/core-ui';
import styled, { hueToColor } from '@benzinga/themetron';
import { noop } from '@benzinga/utils';
import { WidgetLink } from '@benzinga/widget-linking';
import React from 'react';
import { HuePicker } from '../../HuePicker';

interface GroupCreateModalProps {
  group: WidgetLink;
  onClose?: () => void;
  open?: boolean;
  saveGroup: (group: Partial<WidgetLink>) => void;
}

interface GroupCreateModalState {
  title: string;
  hue: number;
  isNewGroup: boolean;
}

const GroupEditModal: React.FC<GroupCreateModalProps> = props => {
  const { group, onClose, open, saveGroup } = props;
  const [state, setState] = React.useState<GroupCreateModalState>({
    hue: 0,
    isNewGroup: false,
    title: '',
  });

  const handleCreate = React.useCallback(() => {
    saveGroup({
      ...group,
      ...{
        color: hueToColor(state.hue),
        title: state.title,
      },
    });
    onClose?.();
  }, [saveGroup, group, state.hue, state.title, onClose]);

  React.useEffect(() => {
    if (!props.open) setState(s => ({ ...s, hue: 0 }));
    else
      setState(s => ({
        ...s,
        hue: props.group.hue ?? 0,
        title: props.group.title,
      }));
  }, [props.group.hue, props.group.title, props.open]);

  return open ? (
    <Modal fullScreen={true} onClose={onClose ?? noop} size={{ alert: true }} title={<span>Edit Tool Groups</span>}>
      <ModalContent>
        <ModalLabel>Enter Tool Group Name</ModalLabel>
        <ModalInput
          onChange={e => {
            setState(s => ({ ...s, title: e.target.value }));
          }}
          placeholder={'New Tool Group'}
          value={state.title}
        ></ModalInput>
        <ModalLabel>Select Tool Group Color</ModalLabel>
        <HuePicker
          hue={state.hue}
          noDeselect={true}
          setHue={hue => {
            setState(s => ({ ...s, hue: hue ?? 0 }));
          }}
          steps={80}
        />
      </ModalContent>
      <ModalButtons>
        <CreateButton onClick={handleCreate}>{'Save'}</CreateButton>
      </ModalButtons>
    </Modal>
  ) : (
    <></>
  );
};

export const useGroupEditModal = (saveGroup: (group: Partial<WidgetLink>) => void) => {
  const [state, setState] = React.useState<{ isOpen: boolean; editingGroup: WidgetLink | undefined }>({
    editingGroup: undefined,
    isOpen: false,
  });

  const onClose = React.useCallback(() => {
    setState(s => ({ ...s, editingGroup: undefined, isOpen: false }));
  }, []);

  const editGroup = React.useCallback((group: WidgetLink | undefined): void => {
    setState(s => ({ ...s, editingGroup: group, isOpen: group != null }));
  }, []);

  const renderModal = React.useCallback((): React.ReactElement => {
    if (!state.editingGroup) return <></>;
    return <GroupEditModal group={state.editingGroup} onClose={onClose} open={state.isOpen} saveGroup={saveGroup} />;
  }, [onClose, saveGroup, state.editingGroup, state.isOpen]);

  return { editGroup, renderModal };
};

const ModalContent = styled.div`
  display: flex;
  padding: 1em;
  flex-direction: column;
`;
const ModalLabel = styled.label`
  font-size: 0.75em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.foregroundMuted};
  display: block;
  margin-bottom: 0.75em;
`;
const ModalButtons = styled.div`
  margin: 1em;
  text-align: right;
`;
const CreateButton = styled.button`
  background-color: ${props => props.theme.colors.brand};
  color: ${props => props.theme.colors.foregroundActive};
  cursor: default;
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  position: relative;
  text-transform: uppercase;
  font-size: 1em;
  padding: 0.75em 1.5em;
  cursor: pointer;
  transition: background-color 0.2s ease;
`;
const ModalInput = styled.input<{ color?: string }>`
  padding: 0 1em;
  position: relative;
  background: ${props => props.color ?? 'transparent'};
  color: inherit;
  width: 100%;
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 1.25em;
  box-sizing: border-box;
  min-height: 39px;
  font-size: 1em;
  outline: none;
`;
